import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Music, LogIn } from 'lucide-react';
import { toast } from 'sonner';
// The shared helper, not a local copy. This file used to define its own
// getApiUrl that returned window.location.origin whenever the hostname
// contained letthebeatbuild.me, which was true on Emergent where one
// deployment served the site and the API. The front end is on Cloudflare Pages
// now and the API is on Fly, so on studio.letthebeatbuild.me that posted the
// login to the static host: 405, empty body, and the JSON parse threw into the
// catch block as "Login failed. Please try again." It worked on pages.dev and
// broke the moment the real domain was attached, which is the worst possible
// time to find it.
import { getApiUrl } from '../utils/api';
import { isReflectStaff, staffHome } from '../utils/reflectStaff';

const AdminLogin = () => {
  const { user, setUser, setIsAuthenticated, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const requestedReturn = searchParams.get('returnTo');
  const reflectReturn = requestedReturn?.startsWith('/admin/reflect') ? requestedReturn : null;

  // Redirect if already authenticated for the requested destination.
  React.useEffect(() => {
    if (isAuthenticated && reflectReturn && isReflectStaff(user)) {
      navigate(reflectReturn, { replace: true });
      return;
    }
    if (isAuthenticated && (isAdmin || isReflectStaff(user))) {
      navigate(staffHome(user), { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate, reflectReturn, user]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        toast.success('Signed in successfully');
        const destination = reflectReturn && isReflectStaff(userData) ? reflectReturn : staffHome(userData);
        navigate(destination, { replace: true, state: { user: userData } });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid email or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-3xl mb-2">Admin Login</h1>
          <p className="text-muted-foreground">Sign in to manage the loop library</p>
        </div>
        
        <div className="rounded-3xl bg-card/50 border border-white/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl bg-background/50 border-white/10"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 rounded-xl bg-background/50 border-white/10"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-black font-semibold h-14 text-lg"
            >
              <LogIn className="w-5 h-5 mr-2" />
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
        
        <div className="text-center mt-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-white"
          >
            <Music className="w-4 h-4 mr-2" />
            Back to Library
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
